import logger from '../../../../utils/logger';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStripe } from '@stripe/react-stripe-js';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  MdError,
  MdRefresh,
  MdCurrencyExchange,
  MdArrowForward,
  MdAccountBalance,
} from 'react-icons/md';
import config from 'config/environment';
import { COMPANIES_QUERY_KEY } from '../../../../hooks/useCompanies';

/**
 * PaymentComplete - Legacy redirect return for disabled crypto checkout.
 *
 * Crypto self-serve is disabled, but the route remains for older redirects.
 *
 * Flow:
 * 1. User returns from an older Stripe redirect
 * 2. Stripe redirects to /payment-complete?payment_intent=pi_xxx&payment_intent_client_secret=xxx&registration=xxx
 * 3. This component verifies the payment status
 * 4. Confirms payment with backend
 * 5. Redirects user to company creation next step
 */
const PaymentComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stripe = useStripe();
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth0();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error' | 'processing'
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const hasVerified = useRef(false);

  // Get URL parameters
  const paymentIntentClientSecret = searchParams.get(
    'payment_intent_client_secret'
  );
  const registrationId = searchParams.get('registration');
  const redirectStatus = searchParams.get('redirect_status');

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) return;

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/auth/sign-in/default', { replace: true });
      return;
    }

    // Prevent duplicate verification
    if (hasVerified.current) return;

    const verifyPayment = async () => {
      // Check for immediate redirect status from Stripe
      if (redirectStatus === 'failed') {
        setStatus('error');
        setErrorMessage(
          'El pago no fue completado. Por favor intenta de nuevo.'
        );
        return;
      }

      if (!stripe || !paymentIntentClientSecret) {
        // If no client secret, maybe the user navigated here directly
        if (!paymentIntentClientSecret) {
          setStatus('error');
          setErrorMessage(
            'No se encontró información del pago. Por favor vuelve a intentar el proceso de pago.'
          );
          return;
        }
        // Stripe not loaded yet, wait
        return;
      }

      hasVerified.current = true;
      setStatus('verifying');

      try {
        // Retrieve the PaymentIntent from Stripe
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(
          paymentIntentClientSecret
        );

        if (error) {
          logger.error('Error retrieving PaymentIntent:', error);
          setStatus('error');
          setErrorMessage(error.message || 'Error al verificar el pago');
          return;
        }

        setPaymentDetails({
          id: paymentIntent.id,
          amount: paymentIntent.amount / 100, // Convert from cents
          currency: paymentIntent.currency.toUpperCase(),
          status: paymentIntent.status,
        });

        // Check payment status
        switch (paymentIntent.status) {
          case 'succeeded':
            // Payment successful - confirm with backend
            setStatus('success');
            await confirmPaymentWithBackend(paymentIntent.id);

            // Invalidate the companies cache so the company list updates without a manual refresh
            queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] });

            // Clear wizard form data from localStorage after successful crypto payment
            // This prevents the old form data from being restored on subsequent visits
            localStorage.removeItem('simplifiedCompanyWizard');
            localStorage.setItem('paymentCompletedAt', Date.now().toString());
            break;

          case 'processing':
            // Payment still processing (common for crypto)
            setStatus('processing');
            // Poll for status or let webhook handle it
            break;

          case 'requires_payment_method':
            setStatus('error');
            setErrorMessage('El pago requiere un método de pago válido.');
            break;

          case 'requires_action':
            setStatus('error');
            setErrorMessage(
              'Se requiere acción adicional para completar el pago.'
            );
            break;

          default:
            setStatus('error');
            setErrorMessage(
              `Estado de pago inesperado: ${paymentIntent.status}`
            );
        }
      } catch (err) {
        logger.error('Payment verification error:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Error al verificar el estado del pago');
      }
    };

    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stripe,
    paymentIntentClientSecret,
    registrationId,
    redirectStatus,
    isAuthenticated,
    authLoading,
    navigate,
  ]);

  // Auto-redirect after successful payment
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }

    // When countdown reaches 0, redirect to company dashboard
    if (status === 'success' && countdown === 0) {
      if (registrationId) {
        navigate(`/empresa/${registrationId}/dashboard`, { replace: true });
      } else {
        navigate('/empresa/empresas', { replace: true });
      }
    }
  }, [status, countdown, registrationId, navigate]);

  /**
   * Confirms payment with the backend to save billing history.
   */
  const confirmPaymentWithBackend = async (paymentIntentId) => {
    try {
      const token = await getAccessTokenSilently();
      const apiUrl = config.apiUrl || 'http://localhost:8080';

      const response = await fetch(`${apiUrl}/api/confirm-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ paymentIntentId }),
      });

      const data = await response.json();
      logger.debug('[PaymentComplete] Backend confirmation:', data);

      if (!response.ok) {
        logger.warn('[PaymentComplete] Backend confirmation failed:', data);
        // Don't fail the flow - webhook will handle it
      }
    } catch (err) {
      logger.warn('[PaymentComplete] Backend confirmation error:', err);
      // Don't fail the flow - webhook will handle it
    }
  };

  /**
   * Handles the continue button click.
   */
  const handleContinue = () => {
    // Navigate to companies list where user can see their newly created company
    navigate('/empresa/empresas', { replace: true });
  };

  /**
   * Handles retry for failed payments.
   */
  const handleRetry = () => {
    if (registrationId) {
      navigate(`/empresa/nueva?registration=${registrationId}`, {
        replace: true,
      });
    } else {
      navigate('/empresa/nueva', { replace: true });
    }
  };

  // Loading state
  if (authLoading || (status === 'verifying' && !stripe)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600" />
          <p className="text-lg text-neutral-600 dark:text-neutral-300">
            Cargando...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950 p-4">
      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -left-1/4 -top-1/4 h-96 w-96 rounded-full bg-[#71DE80]/10 blur-3xl" />
        <div className="absolute -bottom-1/4 -right-1/4 h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md overflow-hidden rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#252525] p-8 shadow-2xl"
      >
        {/* Decorative elements */}
        <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full border border-[#71DE80]/10" />
        <div className="absolute -right-5 -top-5 h-20 w-20 rounded-full border border-[#71DE80]/20" />

        {/* Verifying State */}
        {status === 'verifying' && (
          <div className="relative text-center">
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-purple-500/20 to-purple-600/10 ring-2 ring-purple-500/30">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-purple-500/30 border-t-purple-500" />
            </div>
            <h2 className="mb-2 text-2xl font-bold text-white">
              Verificando pago
            </h2>
            <p className="text-neutral-400">
              Crypto esta proximamente disponible.
            </p>
          </div>
        )}

        {/* Processing State */}
        {status === 'processing' && (
          <div className="relative text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/10 ring-2 ring-amber-500/30"
            >
              <MdCurrencyExchange className="h-12 w-12 text-amber-400" />
            </motion.div>
            <h2 className="mb-2 text-2xl font-bold text-white">
              Crypto proximamente
            </h2>
            <p className="mb-6 text-neutral-400">
              Los pagos con crypto todavia no estan habilitados. Usa tarjeta o
              coordinacion ACH.
            </p>
            {paymentDetails && (
              <div className="mb-6 rounded-2xl bg-neutral-800/50 p-5 ring-1 ring-neutral-700/50">
                <p className="text-sm text-neutral-500">Monto</p>
                <p className="text-3xl font-bold text-white">
                  ${paymentDetails.amount}{' '}
                  <span className="text-lg text-purple-400">USD</span>
                </p>
              </div>
            )}
            <button
              onClick={handleContinue}
              className="group w-full rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 font-bold text-black transition-all hover:shadow-lg hover:shadow-amber-500/25"
            >
              <span className="flex items-center justify-center gap-2">
                Continuar
                <MdArrowForward className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </span>
            </button>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="relative text-center">
            {/* Animated check */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="mx-auto mb-6 flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-[#71DE80]/20 to-[#71DE80]/5 ring-2 ring-[#71DE80]/40"
            >
              <svg className="h-20 w-20" viewBox="0 0 52 52">
                <motion.circle
                  cx="26"
                  cy="26"
                  r="24"
                  fill="none"
                  stroke="#71DE80"
                  strokeWidth="2"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
                <motion.path
                  fill="none"
                  stroke="#71DE80"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14 27l8 8 16-16"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.4, ease: 'easeOut' }}
                />
              </svg>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="mb-2 text-3xl font-black text-[#71DE80]">
                ¡Pago exitoso!
              </h2>
              <p className="mb-6 text-neutral-400">
                Tu pago con crypto ha sido procesado correctamente
              </p>
            </motion.div>

            {paymentDetails && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-6 rounded-2xl bg-gradient-to-br from-neutral-800/80 to-neutral-800/40 p-5 ring-1 ring-[#71DE80]/20"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <p className="text-sm text-neutral-500">Monto pagado</p>
                    <p className="text-3xl font-bold text-white">
                      ${paymentDetails.amount}{' '}
                      <span className="text-lg text-purple-400">USD</span>
                    </p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/20">
                    <MdCurrencyExchange className="h-6 w-6 text-purple-400" />
                  </div>
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <button
                onClick={handleContinue}
                className="group w-full rounded-2xl bg-gradient-to-r from-[#71DE80] to-[#2481C1] px-6 py-4 font-bold text-black shadow-xl shadow-[#71DE80]/20 transition-all hover:shadow-2xl hover:shadow-[#71DE80]/30"
              >
                <span className="flex items-center justify-center gap-2">
                  <MdAccountBalance className="h-5 w-5" />
                  Ver mis empresas
                  <MdArrowForward className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-5 text-center text-sm text-neutral-400"
            >
              <div className="flex items-center justify-center gap-2">
                <div className="h-2 w-2 animate-pulse rounded-full bg-[#71DE80]" />
                Redirigiendo a tu empresa en {countdown}...
              </div>
            </motion.div>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="relative text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-red-500/20 to-red-600/10 ring-2 ring-red-500/30"
            >
              <MdError className="h-12 w-12 text-red-400" />
            </motion.div>
            <h2 className="mb-2 text-2xl font-bold text-white">
              Error en el pago
            </h2>
            <p className="mb-6 text-neutral-400">{errorMessage}</p>
            <button
              onClick={handleRetry}
              className="group flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 font-bold text-white transition-all hover:shadow-lg hover:shadow-red-500/25"
            >
              <MdRefresh className="h-5 w-5" />
              Intentar de nuevo
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentComplete;
